import { Box, Flex, Icon, Text } from '@chakra-ui/react'
import { useEffect } from 'react'
import { CheckCircle, XCircle, Close } from '../../../../utils/constants/icons'

export type GraphToastKind = 'success' | 'error' | 'warning'

export interface GraphToastData {
  kind: GraphToastKind
  title: string
  detail: string
  key?: number
}

interface GraphToastProps {
  toast: GraphToastData | null
  onClose: () => void
  duration?: number
}

const ACCENT: Record<GraphToastKind, { color: string; halo: string; ring: string }> = {
  success: {
    color: '#00B871',
    halo: 'rgba(0, 184, 113, 0.35)',
    ring: 'rgba(0, 184, 113, 0.18)',
  },
  error: {
    color: '#d44000',
    halo: 'rgba(212, 64, 0, 0.35)',
    ring: 'rgba(212, 64, 0, 0.18)',
  },
  warning: {
    color: '#f59e0b',
    halo: 'rgba(245, 158, 11, 0.35)',
    ring: 'rgba(245, 158, 11, 0.18)',
  },
}

const GraphToast = ({ toast, onClose, duration = 4200 }: GraphToastProps) => {
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(onClose, duration)
    return () => clearTimeout(t)
  }, [toast, duration, onClose])

  if (!toast) return null

  const accent = ACCENT[toast.kind]

  return (
    <Box
      key={toast.key}
      position="absolute"
      top={{ base: '12px', md: '20px' }}
      left="50%"
      zIndex={20}
      pointerEvents="none"
      sx={{
        transform: 'translateX(-50%)',
        '@keyframes graph-toast-in': {
          '0%': {
            opacity: 0,
            transform: 'translate(-50%, -14px) scale(0.92)',
            filter: 'blur(6px)',
          },
          '60%': {
            opacity: 1,
            transform: 'translate(-50%, 2px) scale(1.015)',
            filter: 'blur(0px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translate(-50%, 0) scale(1)',
            filter: 'blur(0px)',
          },
        },
        '@keyframes graph-toast-halo': {
          '0%': { transform: 'translate(-50%, -50%) scale(0.6)', opacity: 0.7 },
          '100%': { transform: 'translate(-50%, -50%) scale(2.2)', opacity: 0 },
        },
        '@keyframes graph-toast-sheen': {
          '0%': { backgroundPosition: '-160% 0' },
          '100%': { backgroundPosition: '260% 0' },
        },
        animation: 'graph-toast-in 380ms cubic-bezier(0.22, 1, 0.36, 1) both',
      }}
    >
      <Box
        pointerEvents="auto"
        minW={{ base: 'auto', md: '380px' }}
        maxW={{ base: 'calc(100vw - 24px)', md: '520px' }}
        minH="60px"
        bg="#0F1A2A"
        p="14px 14px 14px 16px"
        borderRadius="12px"
        position="relative"
        overflow="hidden"
        boxShadow={`0px 14px 36px -14px rgba(0,0,0,0.55), 0px 2px 6px 0px rgba(0,0,0,0.12), inset 0 0 0 1px ${accent.ring}`}
      >
        {/* sheen sweep across the dark bg on enter */}
        <Box
          position="absolute"
          inset={0}
          pointerEvents="none"
          sx={{
            background:
              'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.06) 50%, transparent 70%)',
            backgroundSize: '220% 100%',
            animation: 'graph-toast-sheen 1.6s ease-out 120ms 1 both',
          }}
        />

        <Flex gap="8px" justifyContent="space-between" alignItems="flex-start" position="relative">
          <Flex alignItems="flex-start" gap="10px" flex="1" minW={0}>
            <Box
              position="relative"
              w="20px"
              h="20px"
              flexShrink={0}
              mt="1px"
            >
              {/* expanding halo ring on entry */}
              <Box
                position="absolute"
                top="50%"
                left="50%"
                w="20px"
                h="20px"
                borderRadius="full"
                bg={accent.halo}
                sx={{
                  transform: 'translate(-50%, -50%)',
                  animation: 'graph-toast-halo 700ms ease-out 60ms 1 both',
                }}
                pointerEvents="none"
              />
              <Icon
                width="20px"
                height="20px"
                viewBox="0 0 24 24"
                fill="none"
                color={accent.color}
                position="relative"
              >
                {toast.kind === 'success' ? <CheckCircle /> : <XCircle />}
              </Icon>
            </Box>
            <Flex flexDirection="column" justifyContent="center" gap="2px" minW={0} flex="1">
              <Text fontSize="14px" fontWeight="600" color="#FFFFFF" lineHeight="1.4">
                {toast.title}
              </Text>
              <Text fontSize="12px" color="#C1C5C9" lineHeight="1.5" wordBreak="break-word">
                {toast.detail}
              </Text>
            </Flex>
          </Flex>
          <Flex
            as="button"
            aria-label="Close notification"
            onClick={onClose}
            w="32px"
            h="32px"
            ml="4px"
            mt="-4px"
            mr="-4px"
            alignItems="center"
            justifyContent="center"
            borderRadius="8px"
            flexShrink={0}
            cursor="pointer"
            color="#C1C5C9"
            transition="background 150ms ease-out, color 150ms ease-out"
            _hover={{ bg: 'rgba(255,255,255,0.08)', color: '#FFFFFF' }}
            _active={{ bg: 'rgba(255,255,255,0.12)' }}
            _focusVisible={{ boxShadow: '0 0 0 2px rgba(255,255,255,0.35)', outline: 'none' }}
          >
            <Icon width="10px" height="10px" viewBox="0 0 8 8" fill="none">
              <Close />
            </Icon>
          </Flex>
        </Flex>
      </Box>
    </Box>
  )
}

export default GraphToast
